import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, errMsg, input, money } from './costingApi';

export const TemplatePicker = ({ open, onOpenChange, acq, onApplied }) => {
  const [templates, setTemplates] = useState([]);
  const [tid, setTid] = useState('');
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get('/strip-templates').then((ts) => {
      setTemplates(ts);
      const best = ts.find((t) => t.engine_type === acq?.engine_type && (t.model || '').toLowerCase() === (acq?.model || '').toLowerCase()) || ts[0];
      if (best) setTid(best.id);
    }).catch((e) => toast.error(errMsg(e)));
  }, [open, acq]);
  useEffect(() => {
    const t = templates.find((x) => x.id === tid);
    setRows((t?.parts || []).map((p) => ({ ...p, on: true })));
  }, [tid, templates]);
  const set = (i, k, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const picked = rows.filter((r) => r.on);
  const value = picked.reduce((a, r) => a + (parseFloat(r.selling_price) || 0) * (parseInt(r.quantity, 10) || 0), 0);

  const apply = async () => {
    try { const r = await api.post(`/strip-templates/apply/${acq.id}`, { parts: picked }); toast.success(`${r.created} parts added`); onOpenChange(false); onApplied(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveTemplate = async () => {
    try { await api.put(`/strip-templates/${tid}`, { parts: rows.map(({ on, ...p }) => p) }); toast.success('Template updated'); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-slate-900 border-slate-700 text-white max-h-[90vh] overflow-y-auto" data-testid="template-picker">
        <DialogHeader><DialogTitle>Start strip from a template</DialogTitle></DialogHeader>
        <select value={tid} onChange={(e) => setTid(e.target.value)} className={`${input} border rounded h-9 px-2`} data-testid="template-select">
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.parts.length} parts)</option>)}
        </select>
        <div className="flex gap-2 text-xs">
          <button className="text-sky-400 underline" onClick={() => setRows((r) => r.map((x) => ({ ...x, on: true })))}>Tick all</button>
          <button className="text-sky-400 underline" onClick={() => setRows((r) => r.map((x) => ({ ...x, on: false })))}>Untick all</button>
          <span className="text-slate-400">Untick parts that weren't recoverable; edit qty or price before adding.</span>
        </div>
        <div className="border border-slate-700 rounded max-h-[50vh] overflow-y-auto">
          <table className="w-full text-sm">
            <tbody>{rows.map((r, i) => (
              <tr key={`${r.name}-${i}`} className="border-t border-slate-700" data-testid={`template-row-${i}`}>
                <td className="p-1 pl-2"><input type="checkbox" checked={r.on} onChange={(e) => set(i, 'on', e.target.checked)} data-testid={`template-tick-${i}`} /></td>
                <td className="p-1">{r.name}<div className="text-[10px] text-slate-500">{r.category} · {r.price_source}</div></td>
                <td className="p-1 w-20"><Input type="number" min="1" value={r.quantity} onChange={(e) => set(i, 'quantity', e.target.value)} className={`${input} h-8`} /></td>
                <td className="p-1 w-28"><Input type="number" step="0.01" value={r.selling_price} onChange={(e) => set(i, 'selling_price', e.target.value)} className={`${input} h-8`} data-testid={`template-price-${i}`} /></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <p className="text-sm">{picked.length} parts ticked · expected selling value <b className="text-emerald-400">{money(value)}</b></p>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="text-slate-900" onClick={saveTemplate} disabled={!tid} data-testid="template-save-btn">Save edits to template</Button>
          <Button onClick={apply} disabled={!picked.length} className="bg-emerald-600 hover:bg-emerald-700" data-testid="template-apply-btn">Add {picked.length} parts</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
